import { Icon } from "@ui/components/Icon";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";

const searchPill = tv({
  base: [
    "flex h-11 min-w-0 flex-1 cursor-pointer items-center rounded-[80px] bg-white px-3 outline-none",
    "shadow-md",
    "hover:bg-neutral-50 pressed:bg-neutral-100",
    "focus-visible:ring-2 focus-visible:ring-neutral-400",
  ],
});

export interface SearchPillSummary {
  /** Emphasised line, e.g. "Programs in North America". */
  primary: string;
  /** Optional muted second line, e.g. "Any date · 4 guests". */
  secondary?: string;
}

export interface SearchPillProps {
  /** Placeholder shown (with the search icon, left-aligned, muted) when no search is applied. */
  placeholder: string;
  /** Applied-search summary — when set, replaces the placeholder with centred, emphasised text and
   *  drops the search icon (matches the Figma applied state). */
  summary?: SearchPillSummary | null;
  onPress: () => void;
  "aria-label"?: string;
  className?: string;
}

/**
 * A pressable, rounded "search field" that opens a search surface when pressed. Used as the centre
 * slot of the mobile Explore top bar (`TopBarMobile.centerContent`). Two states mirror the design:
 * an empty state (search icon + muted placeholder, left-aligned) and an applied state (centred
 * summary, no icon — a primary line over an optional muted secondary line).
 */
export function SearchPill({ placeholder, summary, onPress, className, ...rest }: SearchPillProps) {
  const ariaLabel = rest["aria-label"] ?? summary?.primary ?? placeholder;

  return (
    <Button aria-label={ariaLabel} className={searchPill({ className })} onPress={onPress}>
      {summary ? (
        <span className="flex min-w-0 flex-1 flex-col items-center justify-center">
          <span className="max-w-full truncate text-sm/5 font-medium text-neutral-900">{summary.primary}</span>
          {summary.secondary ? (
            <span className="max-w-full truncate text-xs/4 text-neutral-600">{summary.secondary}</span>
          ) : null}
        </span>
      ) : (
        <span className="flex min-w-0 flex-1 items-center justify-center gap-2">
          <span aria-hidden="true" className="shrink-0 text-neutral-600 [&>svg]:size-4">
            <Icon name="search" />
          </span>
          <span className="min-w-0 truncate text-base/6 text-neutral-400">{placeholder}</span>
        </span>
      )}
    </Button>
  );
}
